import "./category.css";
import React, { useEffect } from "react";
import { Baby, User, Shield, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FaStar, FaRegStar, FaStarHalfAlt } from "react-icons/fa";
import { setPageMeta } from "../utils/seo";

import childrenImg from "../assets/children.jpg";
import adultImg from "../assets/adult.png";
import essentialImg from "../assets/essential.png";

const Category = () => {
  const navigate = useNavigate();

  useEffect(() => {
    setPageMeta({
      title: "Product Categories | Banat India",
      description:
        "Explore Banat India categories for kids, adults, and sensitive oral care products.",
      keywords:
        "toothbrush categories, kids toothbrush, adult toothbrush, sensitive toothbrush, Banat India",
    });
  }, []);

  const categories = [
    {
      id: "children",
      icon: Baby,
      image: childrenImg,
      title: "Children's Collection",
      subtitle: "Gentle Care for Growing Smiles",
      features: ["Soft Bristles for Kids", "Fun Colorful Designs", "Ergonomic Easy Grip"],
      price: "From ₹85",
    },
    {
      id: "adult",
      icon: User,
      image: adultImg,
      title: "Adult Premium Series",
      subtitle: "Advanced Cleaning Technology",
      features: ["Multi-Level Bristles", "Advanced Stain Removal", "Optimal Gum Protection"],
      price: "From ₹120",
    },
    {
      id: "essential",
      icon: Shield,
      image: essentialImg,
      title: "Essential Care Line",
      subtitle: "Sensitive Teeth Solutions",
      features: ["Ultra-Soft Gentle Bristles", "Sensitive Formula Comfort", "Deep & Soothing Clean"],
      price: "From ₹99",
    },
  ];

  // Filter categories based on search
  const filteredCategories = categories;

  const handleCategoryClick = (categoryId) => {
    // Add smooth transition effect
    const card = document.querySelector(
      `.category-card[data-category="${categoryId}"]`
    );
    if (card) {
      card.style.transform = "scale(0.95)";
      setTimeout(() => {
        card.style.transform = "";
        navigate(`/shop/${categoryId}`);
      }, 200);
    } else {
      navigate(`/shop/${categoryId}`);
    }
  };

  // StarRating component for dynamic stars
  const StarRating = ({ rating, totalStars = 5 }) => {
    const stars = [];
    for (let i = 1; i <= totalStars; i++) {
      if (rating >= i) {
        stars.push(<FaStar key={i} color="#fbbf24" size={20} />);
      } else if (rating >= i - 0.5) {
        stars.push(<FaStarHalfAlt key={i} color="#fbbf24" size={20} />);
      } else {
        stars.push(<FaRegStar key={i} color="#fbbf24" size={20} />);
      }
    }
    return <span style={{ display: "flex", gap: 4 }}>{stars}</span>;
  };

  return (
    <div className="category-container">
      {/* Header Section */}
      <div className="category-header-section">
        <h1 className="category-title">Our Categories</h1>
        <p className="category-subtitle">
          Discover our premium collection of toothbrushes, each category
          thoughtfully designed to meet specific oral care needs with
          cutting-edge technology and superior comfort.
        </p>
      </div>

      {/* Categories Grid */}
      <div className="category-grid">
        {filteredCategories.map((category, index) => {
          let typeClass = "";
          if (category.id === "children") typeClass = "children";
          else if (category.id === "adult") typeClass = "adult";
          else typeClass = "essential";
          return (
            <div
              key={category.id}
              className={`category-card ${typeClass}`}
              onClick={() => handleCategoryClick(category.id)}
              data-category={category.id}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleCategoryClick(category.id);
                }
              }}
            >
              {/* Image taking top half of the card */}
              <div className="category-card-image">
                <img src={category.image} alt={category.title} />
              </div>

              {/* Text / Body details below the image */}
              <div className="category-card-body">
                <h2>{category.title}</h2>
                <div className="subtitle">{category.subtitle}</div>
                
                {/* Features */}
                <div className="category-features">
                  {category.features.map((feature, idx) => (
                    <div key={idx} className="category-feature">
                      <div className="feature-icon-bullet">
                        <ArrowRight className="w-3 h-3 text-white" />
                      </div>
                      <span className="feature-text">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Footer area inside the body */}
                <div className="category-card-footer">
                  <div className="category-price">{category.price}</div>
                  <button
                    className="explore-card-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCategoryClick(category.id);
                    }}
                  >
                    Explore <ArrowRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Social Proof Section */}
      <div className="category-social-proof" style={{ marginTop: 60 }}>
        <div className="social-proof-badge">
          <div className="rating-stars">
            <StarRating rating={4.9} />
          </div>
          <div className="rating-text">
            <span className="rating-score">4.9/5</span>
            <span style={{ marginLeft: 8 }}>from 10,000+ happy customers</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Category;
